import React, { useState } from 'react';
import { Container, Col, Row } from 'react-bootstrap';
import ReactGA from 'react-ga4';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { characterReadingPng } from '../../assets/images';
import { SITE_LINKS } from '../../constants';
import { GoogleAnalyticsEvents } from '../../enum';
import { PlacementTestService } from '../../services/placement-test';
import { Background } from '../../stories/Background';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { Icon } from '../../stories/Icon';

export interface PlacementTestIntroductionPageProps {
  data: {
    token: string;
  };
}

export const PlacementTestIntroductionPage: React.FC<PlacementTestIntroductionPageProps> = (
  props,
) => {
  const __ = useTranslation().t;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState(false);

  const instructions = [
    {
      title: __('readQuestionsCarefully'),
      description: __('notSureReadItAgain'),
    },
    {
      title: __('checkYourComputer'),
      description: __('checkInternetAndAudioMessage'),
    },
    {
      title: __('takeADeepBreath'),
      description: __('readyToTakeTestResultsAfterSubmit'),
    },
  ];

  const onStartTestClick = () => {
    setIsLoading(true);
    ReactGA.event(GoogleAnalyticsEvents.PlacementTestBeginClicked);
    setIsChecked(false);
    window.location.href = SITE_LINKS.PlacementTestPlayerPage;
  };

  const onSkipTestClick = () => {
    setIsLoading(true);
    PlacementTestService.postSkipTest({ token: props.data.token })
      .then(() => {
        ReactGA.event(GoogleAnalyticsEvents.PlacementTestSkipFromInstructionsClicked);
        window.location.href = SITE_LINKS.PlacementTestResultPage;
      })
      .catch(() => {
        alert(__('whopsSomethingWentWrong'));
        setIsLoading(false);
      });
  };

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <StyledContainer>
          <Row>
            <Col lg={6}>
              <Heading>{__('readyForTest')}</Heading>
              <Description>{__('relaxAndTakeYourTimeDoYourBest')}</Description>
              <InstructionsContainer>
                <InstructionsTitle>{__('tips')}:</InstructionsTitle>
                <ul>
                  {instructions.map((instruction) => (
                    <li>
                      <StyledIcon name="check-circle" />
                      <div>
                        <InstructionTitle>{instruction.title}</InstructionTitle>
                        <InstructionDescription>{instruction.description}</InstructionDescription>
                      </div>
                    </li>
                  ))}
                </ul>
              </InstructionsContainer>
            </Col>
            <Col lg={1} />
            <Col lg={5}>
              <ImageContainer>
                <img src={characterReadingPng} alt="Character reading" />
              </ImageContainer>
            </Col>
          </Row>
          <ButtonsSection>
            <Divider />
            <CheckboxLabel>
              <input
                type="checkbox"
                className="min-size-checkbox"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
              <InstructionDescription>
                <Trans alt={__('confirmTimeForTest')} i18nKey="confirmTimeForTest">
                  I confirm that I have at least <strong>20 minutes</strong> to take the placement
                  test without interruption
                </Trans>
              </InstructionDescription>
            </CheckboxLabel>
            <ButtonsContainer>
              <PrimaryButtonCompact
                label={__('startPlacementTest')}
                icon="arrow-right"
                onClick={onStartTestClick}
                disabled={isLoading || !isChecked}
              />
              <InstructionTitle>{__('orYouCan')}</InstructionTitle>
              <ButtonPrimaryOutlineWhite
                label={__('startLevelOneNow')}
                icon="arrow-right"
                onClick={onSkipTestClick}
                disabled={isLoading}
                outline
                variant="primary"
              />
            </ButtonsContainer>
          </ButtonsSection>
        </StyledContainer>
      </Background>
    </>
  );
};

const StyledContainer = styled(Container)`
  padding-top: 60px;
  padding-bottom: 32px;
  && svg {
    padding-left: 8px;
  }
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    padding-top: 32px;
  }
  .min-size-checkbox {
    min-width: 14px;
  }
`;

const Heading = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 12px;
`;

const Description = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black};
`;

const InstructionsContainer = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  color: ${({ theme }) => theme.colors.black};
  margin-top: 24px;

  ul {
    list-style-type: none;
    margin-top: 24px;
    li {
      display: flex;
      margin-bottom: 24px;
      & > div {
        margin-left: 12px;
      }
    }
  }
`;

const InstructionsTitle = styled.div`
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  text-transform: uppercase;
`;

const InstructionTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
`;

const InstructionDescription = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.green};
  height: 24px;
`;

const ImageContainer = styled.div`
  height: 475px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
  }

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.xl}) {
    width: 75%;
    height: auto;
    margin: auto;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  text-align: center;
  gap: 16px;
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    display: inline-flex;
    flex-direction: column;
    width: 100%;
    button {
      width: 100%;
    }
  }
`;

const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  color: ${({ theme }) => theme.colors.dark};
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Divider = styled.div`
  width: 80%;
  height: 1px;
  box-shadow: 0 -1px 0 0 rgba(155, 155, 155, 0.32);
  margin: 2rem auto 0;
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    display: none;
  }
`;

const PrimaryButtonCompact = styled(Button)`
  @media screen and (max-width: ${({ theme }) => theme.screenSizes.sm}) {
    padding: 8px 0px;
  }
`;

const ButtonPrimaryOutlineWhite = styled(PrimaryButtonCompact)`
  background-color: white !important;
`;
